import { memo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { useLogoutMutation } from '../../store/slices/apiSlice';
import { logout } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { getFullName } from '../../utils/auth';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { StatsCard } from '../../components/common/StatsCard';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { PAGE_COLORS } from '../../utils/pageColors';
import { Briefcase, FileText, Settings, Truck } from 'lucide-react';

export const DashboardPage: React.FC = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const [logoutMutation, { isLoading: isLoggingOut }] = useLogoutMutation();

  const handleLogout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await logoutMutation({ refresh: refreshToken }).unwrap();
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Always clear local state and redirect
      dispatch(logout());
      navigate('/login', { replace: true });
    }
  }, [dispatch, navigate, logoutMutation]);

  // User should always be available here since ProtectedRoute handles loading
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Authentication error. Please log in again.</p>
          <Button 
            onClick={() => navigate('/login')}
            className="mt-4"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }





  return (
    <AppShell>
              <PageLayout
          title="Dashboard"
          subtitle={`Hello, ${getFullName(user)}!`}
          bgColor={PAGE_COLORS.dashboard.bgColor}
          textColor={PAGE_COLORS.dashboard.textColor}
          borderColor={PAGE_COLORS.dashboard.borderColor}
          icon={<></>}
        >
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Total Jobs"
            value="24"
            change="+12%"
            changeType="positive"
            icon={<Briefcase className="w-6 h-6" />}
          />
          <StatsCard
            title="Active Orders"
            value="8"
            change="+3"
            changeType="positive"
            icon={<FileText className="w-6 h-6" />}
          />
          <StatsCard
            title="Production"
            value="15"
            change="-2%"
            changeType="negative"
            icon={<Settings className="w-6 h-6" />}
          />
          <StatsCard
            title="Deliveries"
            value="12"
            change="+5"
            changeType="positive"
            icon={<Truck className="w-6 h-6" />}
          />
        </div>

        {/* User Info Card */}
        <ContentCard
          title="User Information"
          subtitle="Your account details and preferences"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <span className="font-medium text-gray-700">Username:</span>
              <span className="ml-2 text-gray-900">{user.username}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Email:</span>
              <span className="ml-2 text-gray-900">{user.email}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded text-sm ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Role:</span>
              <span className={`ml-2 px-2 py-1 rounded text-sm ${user.is_superuser ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                {user.is_superuser ? 'Superadmin' : 'User'}
              </span>
            </div>
          </div>
          <div className="mt-6">
            <Button
              variant="danger"
              onClick={handleLogout}
              loading={isLoggingOut}
            >
              Logout
            </Button>
          </div>
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
});

DashboardPage.displayName = 'DashboardPage';